import { useMemo, useState } from 'react'
import { InventoryLocation } from 'inventory-web'
import { heapEvent } from '@equipmentshare/heaper'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'

import {
	InventoryLocationWarehouseQuery,
	WarehouseExecutionSystem,
	useInventoryLocationWarehouseQuery,
	useUpdateInventoryLocationWarehouseMutation,
} from '../../../../../../graphql'
import { useShowError, useShowSuccess } from 'hooks/useToast'
import parseZipCodes from './parseZipCodes'

const MAX_INVALID_SHOWN = 5

export type WarehouseSettingsModalProps = {
	onCancel: () => void,
	onSuccess: () => void,
	inventoryLocation: InventoryLocation,
	updateAfterEdit: ( updatedInventoryLocation: InventoryLocation ) => void,
}

type WarehouseConfiguration = InventoryLocationWarehouseQuery['inventoryLocations']['edges'][number]['node']

type WarehouseSettingsFormProps = WarehouseSettingsModalProps & {
	current: WarehouseConfiguration,
}

function WarehouseSettingsForm( { onCancel, onSuccess, inventoryLocation, updateAfterEdit, current }: WarehouseSettingsFormProps ) {
	const [ isWarehouse, setIsWarehouse ] = useState( current.isWarehouse )
	const [ system, setSystem ] = useState<WarehouseExecutionSystem | undefined>( current.warehouse?.warehouseExecutionSystem )
	const [ zipCodesText, setZipCodesText ] = useState( ( current.warehouse?.servedZipCodes ?? [] ).join( '\n' ) )
	const [ showErrors, setShowErrors ] = useState( false )
	const [ isSaving, setIsSaving ] = useState( false )
	const [ updateWarehouse ] = useUpdateInventoryLocationWarehouseMutation()
	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const { zipCodes, invalid } = useMemo( () => parseZipCodes( zipCodesText ), [ zipCodesText ] )
	// Only mini-DCs (picked by hand) are routed to by zip code.
	const servesZipCodes = isWarehouse && system === WarehouseExecutionSystem.Manual
	const systemError = isWarehouse && !system ? 'Choose who picks this warehouse\'s orders.' : undefined
	const zipCodesError = servesZipCodes && invalid.length > 0
		? `Zip codes must be exactly 5 digits: ${invalid.slice( 0, MAX_INVALID_SHOWN ).join( ', ' )}${invalid.length > MAX_INVALID_SHOWN ? ` and ${invalid.length - MAX_INVALID_SHOWN} more` : ''}`
		: undefined
	const isRemoving = current.isWarehouse && !isWarehouse
	const isChangingSystem = current.isWarehouse && isWarehouse && Boolean( system ) && system !== current.warehouse?.warehouseExecutionSystem

	const handleCancel = () => {
		heapEvent( 'Inventory - Warehouse Settings Modal - Click - Cancel', {} )
		onCancel()
	}

	const handleSubmit = async() => {
		heapEvent( 'Inventory - Warehouse Settings Modal - Click - Submit', {} )
		if ( systemError || zipCodesError ) {
			setShowErrors( true )
			return
		}

		setIsSaving( true )
		const warehouse = isWarehouse && system
			? { warehouseExecutionSystem: system, ...( servesZipCodes ? { servedZipCodes: zipCodes } : {} ) }
			: null

		try {
			const result = await updateWarehouse( { variables: {
				inventoryLocationId: parseInt( inventoryLocation.id, 10 ),
				warehouse,
			} } )
			const payload = result.data?.updateInventoryLocation
			if ( payload?.success && payload.inventoryLocation ) {
				updateAfterEdit( payload.inventoryLocation )
				showSuccess( `updateWarehouseSettingsSuccess${Date.now()}`, 'Warehouse settings saved.' )
				onSuccess()
			} else {
				showError( `updateWarehouseSettingsError${Date.now()}`, payload?.message ?? 'Sorry, an error occurred' )
				setIsSaving( false )
			}
		} catch ( e: unknown ) {
			// GraphQL errors are already toasted by the Apollo error link.
			setIsSaving( false )
		}
	}

	return (
		<>
			<DialogContent>
				<Stack spacing={2}>
					<FormControlLabel
						control={
							<Switch
								checked={isWarehouse}
								onChange={( event ) => setIsWarehouse( event.target.checked )}
							/>
						}
						label="This store is a warehouse"
					/>
					{ isWarehouse && (
						<FormControl error={showErrors && Boolean( systemError )}>
							<FormLabel id="warehouse-execution-system-label">Picked by</FormLabel>
							<RadioGroup
								aria-labelledby="warehouse-execution-system-label"
								value={system ?? ''}
								onChange={( event ) => setSystem( event.target.value as WarehouseExecutionSystem )}
							>
								<FormControlLabel value={WarehouseExecutionSystem.Numina} control={<Radio />} label="Numina" />
								<FormControlLabel value={WarehouseExecutionSystem.Manual} control={<Radio />} label="Manual" />
							</RadioGroup>
							{ showErrors && systemError && <FormHelperText>{systemError}</FormHelperText> }
						</FormControl>
					) }
					{ servesZipCodes && (
						<TextField
							label="Served zip codes"
							multiline
							minRows={6}
							maxRows={12}
							value={zipCodesText}
							onChange={( event ) => setZipCodesText( event.target.value )}
							error={showErrors && Boolean( zipCodesError )}
							helperText={showErrors && zipCodesError
								? zipCodesError
								: `${zipCodes.length} zip codes. Stock requests from branches in these zip codes are routed here first. Paste them one per line or separated by commas.`}
						/>
					) }
					{ isRemoving && (
						<Alert severity="warning">
							Saving stops {inventoryLocation.name} being a warehouse and deletes its served zip codes.
							Open fulfillment and put-away orders finish as they were sent; new ones can no longer be sent here.
						</Alert>
					) }
					{ isChangingSystem && (
						<Alert severity="info">
							Only orders sent from now on use the new system. Open orders finish as they were sent.
						</Alert>
					) }
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel} color="inherit" disabled={isSaving}>Cancel</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={isSaving}
					startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
				>
					Save
				</Button>
			</DialogActions>
		</>
	)
}

export default function WarehouseSettingsModal( props: WarehouseSettingsModalProps ) {
	const { data, loading } = useInventoryLocationWarehouseQuery( {
		variables: { inventoryLocationId: props.inventoryLocation.id },
		fetchPolicy: 'network-only',
	} )
	const current = data?.inventoryLocations.edges[ 0 ]?.node

	if ( !current ) {
		return (
			<>
				<DialogContent>
					{ loading ? (
						<Box display="flex" justifyContent="center" py={3}>
							<CircularProgress />
						</Box>
					) : (
						<Alert severity="error">Couldn&apos;t load this store&apos;s warehouse settings.</Alert>
					) }
				</DialogContent>
				<DialogActions>
					<Button onClick={props.onCancel} color="inherit">Cancel</Button>
				</DialogActions>
			</>
		)
	}

	// Mounted only once loaded, so the form's initial state is the saved configuration.
	return <WarehouseSettingsForm {...props} current={current} />
}
